/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { Ionicons } from '@expo/vector-icons';
import { whatsAppHref } from '@shared/phone';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import type { CustomerAccountRow } from '../domain/customerTypes';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader'
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal'
import type { OperationalConfirmModalProps } from '../../shared/modals/OperationalConfirmModal'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { CustomerPendingSalesPanel, type CustomerPendingSaleRow } from './CustomerPendingSalesPanel';
import { CustomerCreditGauge } from './CustomerCreditGauge';
import { CustomerAgingBreakdown, type AgingBuckets } from './CustomerAgingBreakdown';
import { CustomerDetailDunningPanel } from './CustomerDetailDunningPanel';
import { CustomerDetailContactInfo } from './CustomerDetailContactInfo';
import { CustomerDetailCommercialTerms } from './CustomerDetailCommercialTerms';
import { CustomerDetailPortalInfo } from './CustomerDetailPortalInfo';
import { CustomerDetailNotesSection } from './CustomerDetailNotesSection';
import { CustomerLoyaltyPointsPanel } from './CustomerLoyaltyPointsPanel';
import { CustomerPackagesPanel } from './CustomerPackagesPanel';
import { CustomerAppointmentHistorySection } from './CustomerAppointmentHistorySection';
import { CustomerServiceRecordsSection } from './CustomerServiceRecordsSection';
import { CustomerNotesSection } from './CustomerNotesSection';
import { CustomerTasksSection } from './CustomerTasksSection';
import { CustomerSalesHistorySection } from './CustomerSalesHistorySection';
import { CustomerChecksSection } from './CustomerChecksSection';
import { getDunningLevel, getOverdueDays } from '../application/customerDunning';
import { hasCustomerCreditLimit } from '../application/customerCreditLimit';
import type { CustomerAppointmentRow, CustomerAppointmentSummary, CustomerSalesWindows } from '../screens/hooks/useCustomerAppointments';
import type { CustomerSalesSummary } from '../infra/customerRepository';
import type { CustomerSaleRow } from '../infra/customer360Repo';
import type { ServiceRecord, CustomerNote } from '../screens/hooks/useCustomerServiceRecords';
import type { CustomerTask } from '../screens/hooks/useCustomerTasks';

type Props = {
  visible: boolean;
  customer: CustomerAccountRow | null;
  canEdit: boolean;
  canBlock: boolean;
  canCollect: boolean;
  canAdjustDebt: boolean;
  linkingCustomerId: string | null;
  formatMoney: (n: number) => string;
  onClose: () => void;
  onOpenMovements: (customer: CustomerAccountRow) => void;
  onOpenHistory: (customer: CustomerAccountRow) => void;
  onEdit: (customer: CustomerAccountRow) => void;
  onWhatsApp: (customer: CustomerAccountRow) => void;
  onShare: (customer: CustomerAccountRow) => void;
  onToggleBlock: (customer: CustomerAccountRow) => void;
  onExportStatement?: (customer: CustomerAccountRow) => void;
  confirmOverlay?: Omit<OperationalConfirmModalProps, 'visible' | 'presentation'> | null;
  onCollectSale?: (sale: CustomerPendingSaleRow) => void;
  onViewSale?: (saleId: string) => void;
  pendingSales: CustomerPendingSaleRow[];
  loadingPendingSales: boolean;
  agingBuckets: AgingBuckets | null;
  appointments: CustomerAppointmentRow[];
  appointmentsLoading: boolean;
  appointmentSummary: CustomerAppointmentSummary;
  salesSummary?: CustomerSalesSummary | null;
  salesWindows?: CustomerSalesWindows | null;
  customerSales?: CustomerSaleRow[];
  customerSalesLoading?: boolean;
  salesPage?: number;
  salesHasMore?: boolean;
  onLoadSalesPage?: (page: number) => void;
  apptPage?: number;
  apptHasMore?: boolean;
  onLoadApptPage?: (page: number) => void;
  branches?: { id: string; name: string }[];
  serviceRecords?: ServiceRecord[];
  serviceRecordsLoading?: boolean;
  customerNotes?: CustomerNote[];
  customerNotesLoading?: boolean;
  notesPage?: number;
  notesHasMore?: boolean;
  onLoadNotesPage?: (page: number) => void;
  onAddNote?: (body: string, isAlert: boolean) => Promise<void>;
  onArchiveNote?: (noteId: string) => void;
  customerTasks?: CustomerTask[];
  customerTasksLoading?: boolean;
  tasksPage?: number;
  tasksHasMore?: boolean;
  onLoadTasksPage?: (page: number) => void;
  onAddTask?: (title: string, kind: 'call' | 'message' | 'visit' | 'task', dueDate: string | null) => Promise<void>;
  onCompleteTask?: (taskId: string) => void;
  onArchiveTask?: (taskId: string) => void;
  layoutWide?: boolean;
};

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
}

type DetailSectionKey = 'resumen' | 'cuenta' | 'ventas' | 'actividad' | 'notas' | 'tareas';

export function CustomerDetailModal({
  visible,
  customer,
  canEdit,
  canBlock,
  canCollect,
  canAdjustDebt,
  linkingCustomerId,
  formatMoney,
  onClose,
  onOpenMovements,
  onOpenHistory,
  onEdit,
  onWhatsApp,
  onShare,
  onToggleBlock,
  onExportStatement,
  confirmOverlay = null,
  onCollectSale,
  onViewSale,
  pendingSales,
  loadingPendingSales,
  agingBuckets,
  appointments,
  appointmentsLoading,
  appointmentSummary,
  salesSummary,
  salesWindows,
  customerSales = [],
  customerSalesLoading = false,
  salesPage = 0,
  salesHasMore = false,
  onLoadSalesPage,
  apptPage = 0,
  apptHasMore = false,
  onLoadApptPage,
  branches,
  serviceRecords = [],
  serviceRecordsLoading = false,
  customerNotes = [],
  customerNotesLoading = false,
  notesPage = 0,
  notesHasMore = false,
  onLoadNotesPage,
  onAddNote,
  onArchiveNote,
  customerTasks = [],
  customerTasksLoading = false,
  tasksPage = 0,
  tasksHasMore = false,
  onLoadTasksPage,
  onAddTask,
  onCompleteTask,
  onArchiveTask,
  layoutWide = false,
}: Props) {
  const modalBackdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();
  const { width: winW, height: winH } = useWindowDimensions();
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState<DetailSectionKey>('resumen');

  useEffect(() => {
    setNotesExpanded(false);
    setActiveSection('resumen');
  }, [customer?.id]);

  if (!visible || !customer) {
    return null;
  }

  const hasPhone = !!whatsAppHref(customer.phone);
  const hasBalance = customer.balance > 0;
  const isLinking = linkingCustomerId === customer.id;
  const isBlocked = customer.blocked;
  const balanceTone =
    customer.balance > 0
      ? { color: ticketColors.bad }
      : customer.balance < 0
        ? { color: ticketColors.ok }
        : null;

  const dismissDetail = () => {
    if (confirmOverlay) {
      confirmOverlay.onClose();
      return;
    }
    onClose();
  };

  const dunningLevel = getDunningLevel(customer);
  const overdueDays = getOverdueDays(customer);
  const statusPillStyle = isBlocked
    ? [styles.statusPillMobile, styles.statusPillBlocked]
    : dunningLevel !== 'none'
      ? [styles.statusPillMobile, styles.statusPillBlocked]
      : styles.statusPillMobile;
  const statusTextStyle = isBlocked
    ? styles.statusPillTextBlocked
    : dunningLevel !== 'none'
      ? styles.statusPillTextBlocked
      : styles.statusPillTextOk;
  const statusLabel = isBlocked
    ? 'Bloqueado'
    : dunningLevel !== 'none'
      ? `Vencido ${overdueDays ?? 0}d`
      : customer.balance < 0
        ? 'A favor'
        : 'Activo';

  // ── Shared content blocks (mobile flag applies clean design overrides) ──
  const heroBlock = (mobile: boolean) => (
    <View style={mobile ? styles.heroBandMobile : modalExperience.detailMetricHero}>
      <View style={modalExperience.detailMetricMain}>
        {mobile ? (
          <Text style={styles.heroEyebrowMobile}>Cliente</Text>
        ) : (
          <Text style={modalExperience.detailMetricLabel}>Cliente</Text>
        )}
        <Text
          style={mobile ? styles.heroNameMobile : modalExperience.heroTitle}
          numberOfLines={2}
          ellipsizeMode="tail"
        >
          {customer.name}
        </Text>
        <Text style={[mobile ? styles.heroBalanceMobile : modalExperience.detailMetricValue, balanceTone]}>
          {formatMoney(customer.balance)}
        </Text>
        <Text style={mobile ? styles.heroMetaMobile : modalExperience.detailMetricMeta}>
          {hasCustomerCreditLimit(customer.creditLimit)
            ? `Límite ${formatMoney(customer.creditLimit)}`
            : 'Sin límite'}
        </Text>
        {salesSummary && salesSummary.salesCount > 0 ? (
          <View style={mobile ? styles.heroStatsMobile : styles.heroStatsDesktop} testID="customers.detail.hero-stats">
            <View style={styles.heroStatItem}>
              <Text style={styles.heroStatValue}>{formatMoney(salesSummary.totalSpent)}</Text>
              <Text style={styles.heroStatLabel}>gasto total</Text>
            </View>
            <View style={styles.heroStatDivider} />
            <View style={styles.heroStatItem}>
              <Text style={styles.heroStatValue}>{salesSummary.salesCount}</Text>
              <Text style={styles.heroStatLabel}>
                {salesSummary.salesCount === 1 ? 'venta' : 'ventas'}
              </Text>
            </View>
            {salesSummary.lastSaleAt ? (
              <>
                <View style={styles.heroStatDivider} />
                <View style={styles.heroStatItem}>
                  <Text style={styles.heroStatValue}>{formatShortDate(salesSummary.lastSaleAt)}</Text>
                  <Text style={styles.heroStatLabel}>última compra</Text>
                </View>
              </>
            ) : null}
          </View>
        ) : null}
      </View>
      <View style={mobile ? statusPillStyle : [modalExperience.statusPill, isBlocked ? modalExperience.statusPillCritical : modalExperience.statusPillOk]}>
        <Text style={mobile ? statusTextStyle : (isBlocked ? modalExperience.statusPillTextCritical : modalExperience.statusPillTextOk)}>
          {statusLabel}
        </Text>
      </View>
    </View>
  );

  const gaugeBlock = (mobile: boolean) => (
    <View style={mobile ? styles.infoPanelMobile : styles.metricCard}>
      <CustomerCreditGauge
        balance={customer.balance}
        creditLimit={customer.creditLimit}
        formatMoney={formatMoney}
      />
    </View>
  );

  const agingBlock = (mobile: boolean) => agingBuckets ? (
    <View style={mobile ? styles.infoPanelMobile : styles.flatSection}>
      <CustomerAgingBreakdown buckets={agingBuckets} formatMoney={formatMoney} />
    </View>
  ) : null;

  const dunningBlock = (mobile: boolean) => (
    <CustomerDetailDunningPanel
      customer={customer}
      hasPhone={hasPhone}
      formatMoney={formatMoney}
      onSendReminder={canCollect || canEdit ? () => onWhatsApp(customer) : undefined}
    />
  );

  const contactBlock = (mobile: boolean) => (
    <CustomerDetailContactInfo customer={customer} />
  );

  const termsBlock = (mobile: boolean) => (
    <CustomerDetailCommercialTerms customer={customer} />
  );

  const notesBlock = (mobile: boolean) => (
    <CustomerDetailNotesSection
      customer={customer}
      notesExpanded={notesExpanded}
      onToggleNotes={() => setNotesExpanded((v) => !v)}
    />
  );

  const portalBlock = (mobile: boolean) => (
    <CustomerDetailPortalInfo customer={customer} formatMoney={formatMoney} />
  );

  const loyaltyBlock = (mobile: boolean) => (
    <CustomerLoyaltyPointsPanel customerId={customer.id} mobile={mobile} />
  );

  const packagesBlock = (mobile: boolean) => (
    <CustomerPackagesPanel customerId={customer.id} mobile={mobile} />
  );

  const salesBlock = (mobile: boolean) => (
    <CustomerPendingSalesPanel
      customerId={customer.id}
      sales={pendingSales}
      loading={loadingPendingSales}
      formatMoney={formatMoney}
      onPressSale={onViewSale}
      onCollectSale={canCollect ? onCollectSale : undefined}
    />
  );

  const appointmentsBlock = (mobile: boolean) => (
    <CustomerAppointmentHistorySection
      appointments={appointments}
      loading={appointmentsLoading}
      summary={appointmentSummary}
      totalCount={appointmentSummary.totalVisits + appointmentSummary.cancelledCount + appointmentSummary.noShowCount + (appointmentSummary.nextAppointmentAt ? 1 : 0)}
      apptPage={apptPage}
      apptHasMore={apptHasMore}
      onLoadApptPage={onLoadApptPage}
      branches={branches}
      mobile={mobile}
      onViewSale={onViewSale}
    />
  );

  const serviceRecordsBlock = (mobile: boolean) => (
    <CustomerServiceRecordsSection
      records={serviceRecords}
      loading={serviceRecordsLoading}
      mobile={mobile}
    />
  );

  const salesHistoryBlock = (mobile: boolean) => (
    <CustomerSalesHistorySection
      sales={customerSales}
      loading={customerSalesLoading}
      page={salesPage}
      hasMore={salesHasMore}
      pageSize={5}
      onLoadPage={onLoadSalesPage ?? (() => {})}
      totalCount={salesSummary?.salesCount}
      salesSummary={salesSummary}
      salesWindows={salesWindows}
      mobile={mobile}
      onViewSale={onViewSale}
    />
  );

  const checksBlock = (mobile: boolean) => (
    <CustomerChecksSection
      customerId={customer.id}
      customerName={customer.name}
      formatMoney={formatMoney}
      mobile={mobile}
      reloadToken={customer.updatedAt}
    />
  );

  const customerNotesBlock = (mobile: boolean) => (
    <CustomerNotesSection
      notes={customerNotes}
      loading={customerNotesLoading}
      canEdit={canEdit}
      notesPage={notesPage}
      notesHasMore={notesHasMore}
      onLoadNotesPage={onLoadNotesPage}
      onAddNote={onAddNote ?? (() => Promise.resolve())}
      onArchiveNote={onArchiveNote ?? (() => {})}
      mobile={mobile}
    />
  );

  const tasksBlock = (mobile: boolean) => (
    <CustomerTasksSection
      tasks={customerTasks}
      loading={customerTasksLoading}
      canEdit={!!onAddTask}
      page={tasksPage}
      hasMore={tasksHasMore}
      onLoadPage={onLoadTasksPage}
      onAddTask={onAddTask ?? (() => Promise.resolve())}
      onCompleteTask={onCompleteTask ?? (() => {})}
      onArchiveTask={onArchiveTask ?? (() => {})}
      mobile={mobile}
    />
  );

  const actionsBlock = (mobile: boolean) => {
    const btnBase = mobile ? styles.actionBtnMobile : modalExperience.detailActionBtn;
    const btnPrimary = mobile
      ? [styles.actionBtnMobile, styles.actionBtnPrimaryMobile]
      : [modalExperience.detailActionBtn, modalExperience.detailActionBtnPrimary];
    const btnDanger = mobile
      ? [styles.actionBtnMobile, styles.actionBtnDangerMobile]
      : [modalExperience.detailActionBtn, modalExperience.detailActionBtnDanger];
    const btnText = mobile ? styles.actionTextMobile : modalExperience.detailActionText;
    const btnTextPrimary = mobile ? styles.actionTextPrimaryMobile : modalExperience.detailActionTextPrimary;
    const btnTextDanger = mobile ? styles.actionTextDangerMobile : modalExperience.detailActionTextDanger;
    const iconSize = mobile ? 16 : 18;

    const canOperate = canCollect || canAdjustDebt;

    return (
      <>
        {canOperate ? (
          <TouchableOpacity
            style={[...btnPrimary, styles.actionBtnFull]}
            onPress={() => onOpenMovements(customer)}
            accessibilityRole="button"
            accessibilityLabel="Cobrar cuenta"
            testID="customers.detail.charge"
          >
            <Ionicons name="cash-outline" size={iconSize} color={ticketColors.goldInk} />
            <Text style={btnTextPrimary}>Cobrar cuenta</Text>
          </TouchableOpacity>
        ) : null}

        <TouchableOpacity
          style={btnBase}
          onPress={() => onOpenHistory(customer)}
          accessibilityRole="button"
          accessibilityLabel="Ver historial completo"
        >
          <Ionicons name="time-outline" size={iconSize} color={ticketColors.ink} />
          <Text style={btnText}>Historial</Text>
        </TouchableOpacity>

        {canEdit ? (
          <TouchableOpacity
            style={btnBase}
            onPress={() => onEdit(customer)}
            accessibilityRole="button"
            accessibilityLabel="Editar datos del cliente"
            testID="customers.detail.edit"
          >
            <Ionicons name="create-outline" size={iconSize} color={ticketColors.ink} />
            <Text style={btnText}>Editar</Text>
          </TouchableOpacity>
        ) : null}

        {(canCollect || canEdit) ? (
          <TouchableOpacity
            style={[btnBase, (!hasPhone || !hasBalance || isLinking) && posUi.disabledCard]}
            onPress={() => onWhatsApp(customer)}
            disabled={!hasPhone || !hasBalance || isLinking}
            accessibilityRole="button"
            accessibilityLabel="Enviar recordatorio por WhatsApp"
          >
            <Ionicons name="logo-whatsapp" size={iconSize} color={ticketColors.ink} />
            <Text style={btnText}>WhatsApp</Text>
          </TouchableOpacity>
        ) : null}

        {(canCollect || canEdit) ? (
          <TouchableOpacity
            style={[btnBase, (!hasBalance || isLinking) && posUi.disabledCard]}
            onPress={() => onShare(customer)}
            disabled={!hasBalance || isLinking}
            accessibilityRole="button"
            accessibilityLabel="Compartir link de pago"
          >
            <Ionicons name="share-outline" size={iconSize} color={ticketColors.ink} />
            <Text style={btnText}>Compartir link</Text>
          </TouchableOpacity>
        ) : null}

        {canBlock ? (
          <TouchableOpacity
            style={isBlocked ? btnPrimary : btnDanger}
            onPress={() => onToggleBlock(customer)}
            accessibilityRole="button"
            accessibilityLabel={isBlocked ? 'Desbloquear cliente' : 'Bloquear cliente'}
          >
            <Ionicons
              name={isBlocked ? 'lock-open-outline' : 'ban-outline'}
              size={iconSize}
              color={isBlocked ? ticketColors.goldInk : ticketColors.bad}
            />
            <Text style={isBlocked ? btnTextPrimary : btnTextDanger}>
              {isBlocked ? 'Desbloquear' : 'Bloquear'}
            </Text>
          </TouchableOpacity>
        ) : null}

        {onExportStatement ? (
          <TouchableOpacity
            style={[btnBase, styles.actionBtnFull]}
            onPress={() => onExportStatement(customer)}
            accessibilityRole="button"
            accessibilityLabel="Descargar estado de cuenta"
          >
            <Ionicons name="download-outline" size={iconSize} color={ticketColors.ink} />
            <Text style={btnText}>Estado de cuenta</Text>
          </TouchableOpacity>
        ) : null}
      </>
    );
  };

  // ── Wide: centered card con rail de secciones + acciones siempre visibles ──
  if (layoutWide) {
    const modalCardMaxWidth = Math.min(1040, winW - 48);
    const modalCardMaxHeight = winH * 0.92;
    const detailSections: { key: DetailSectionKey; label: string; count?: number }[] = [
      { key: 'resumen', label: 'Resumen' },
      { key: 'cuenta', label: 'Cuenta', count: pendingSales.length || undefined },
      { key: 'ventas', label: 'Ventas', count: (salesSummary?.salesCount ?? customerSales.length) || undefined },
      {
        key: 'actividad',
        label: 'Actividad',
        count: appointments.length + serviceRecords.length || undefined,
      },
      { key: 'notas', label: 'Notas', count: customerNotes.length || undefined },
      { key: 'tareas', label: 'Tareas', count: customerTasks.length || undefined },
    ];
    const sectionContent: Record<DetailSectionKey, React.ReactNode> = {
      resumen: (
        <>
          {heroBlock(false)}
          {contactBlock(false)}
          {termsBlock(false)}
          {gaugeBlock(false)}
          {agingBlock(false)}
          {dunningBlock(false)}
          {notesBlock(false)}
          {portalBlock(false)}
        </>
      ),
      cuenta: (
        <>
          {salesBlock(false)}
          {checksBlock(false)}
        </>
      ),
      ventas: salesHistoryBlock(false),
      actividad: (
        <>
          {appointmentsBlock(false)}
          {serviceRecordsBlock(false)}
          {loyaltyBlock(false)}
          {packagesBlock(false)}
        </>
      ),
      notas: customerNotesBlock(false),
      tareas: tasksBlock(false),
    };
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={dismissDetail} accessibilityViewIsModal>
        <View style={[posUi.modalBackdrop, modalExperience.modalRoot, modalBackdropPadding]}>
          <View
            style={[
              posUi.modalCard,
              modalShellShared.modalCardShell,
              styles.detailCard,
              styles.detailCardColumn,
              { maxWidth: modalCardMaxWidth, maxHeight: modalCardMaxHeight },
            ]}
          >
            <TicketModalHeader
              kicker="Clientes"
              title={customer.name}
              subtitle={salesSummary?.lastSaleAt ? `Última compra ${formatShortDate(salesSummary.lastSaleAt)}` : undefined}
              onClose={dismissDetail}
            />
            <View style={styles.railWrap}>
              <View style={styles.rail}>
                {detailSections.map((s) => {
                  const active = activeSection === s.key;
                  return (
                    <TouchableOpacity
                      key={s.key}
                      style={[styles.railItem, active && styles.railItemActive]}
                      onPress={() => setActiveSection(s.key)}
                      accessibilityRole="button"
                      accessibilityLabel={`Sección ${s.label}`}
                      accessibilityState={{ selected: active }}
                      testID={`customers.detail.section.${s.key}`}
                    >
                      <Text style={[styles.railItemText, active && styles.railItemTextActive]}>
                        {s.label}
                      </Text>
                      {s.count != null ? (
                        <Text style={[styles.railCount, active && styles.railCountActive]}>
                          {s.count}
                        </Text>
                      ) : null}
                    </TouchableOpacity>
                  );
                })}
              </View>
              <ScrollView
                style={styles.modalScroll}
                contentContainerStyle={styles.modalScrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
                nestedScrollEnabled
              >
                {sectionContent[activeSection]}
              </ScrollView>
            </View>
            <View style={styles.actionsDockDesktop}>
              <View style={modalExperience.detailActionsGrid}>{actionsBlock(false)}</View>
            </View>
          </View>
          {confirmOverlay ? (
            <OperationalConfirmModal visible presentation="overlay" {...confirmOverlay} />
          ) : null}
        </View>
      </Modal>
    );
  }

  // ── Mobile: bottom sheet (View shell — Pressable envolviendo el card bloquea scroll) ──
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={dismissDetail} accessibilityViewIsModal>
      <View style={styles.sheetBackdrop}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={dismissDetail}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View style={[styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
          <TicketModalHeader
            kicker="Clientes"
            title={customer.name}
            subtitle={salesSummary?.lastSaleAt ? `Última compra ${formatShortDate(salesSummary.lastSaleAt)}` : undefined}
            onClose={dismissDetail}
            compact
          />

          <ScrollView
            style={styles.sheetScroll}
            contentContainerStyle={styles.sheetScrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
            nestedScrollEnabled
          >
            {heroBlock(true)}
            {gaugeBlock(true)}
            {agingBlock(true)}
            {dunningBlock(true)}
            {contactBlock(true)}
            {termsBlock(true)}
            {loyaltyBlock(true)}
            {packagesBlock(true)}
            {notesBlock(true)}
            {portalBlock(true)}
            {salesBlock(true)}
            {appointmentsBlock(true)}
            {salesHistoryBlock(true)}
            {checksBlock(true)}
            {serviceRecordsBlock(true)}
            {customerNotesBlock(true)}
            {tasksBlock(true)}
          </ScrollView>

          <View style={styles.sheetActionsDock}>
            <Text style={styles.sectionLabelMobile}>Acciones</Text>
            <View style={modalExperience.detailActionsGrid}>{actionsBlock(true)}</View>
          </View>
        </View>
        {confirmOverlay ? (
          <OperationalConfirmModal visible presentation="overlay" {...confirmOverlay} />
        ) : null}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  // ── Wide (centered card + rail de secciones) ──
  detailCard: {
    width: '100%',
    alignSelf: 'center',
    overflow: 'hidden',
    padding: 0,
    gap: 0,
  },
  detailCardColumn: {
    flexDirection: 'column',
  },
  railWrap: {
    flexDirection: 'row',
    flexShrink: 1,
    minHeight: 0,
  },
  rail: {
    width: 158,
    flexShrink: 0,
    borderRightWidth: 1,
    borderRightColor: ticketColors.ruleSoft,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.md,
    gap: 2,
  },
  railItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
  },
  railItemActive: {
    backgroundColor: ticketColors.brandSoft,
  },
  railItemText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  railItemTextActive: {
    color: ticketColors.brandInk,
    fontWeight: '700',
  },
  railCount: {
    marginLeft: 'auto',
    fontSize: 10.5,
    color: ticketColors.ink3,
    backgroundColor: ticketColors.tile,
    borderRadius: 999,
    paddingVertical: 1,
    paddingHorizontal: 6,
    overflow: 'hidden',
  },
  railCountActive: {
    backgroundColor: ticketColors.brandPrimary,
    color: ticketColors.goldInk,
  },
  modalScroll: {
    flex: 1,
    flexShrink: 1,
  },
  modalScrollContent: {
    gap: spacing.lg,
    paddingHorizontal: 20,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
  },
  /** Card blanca con borde (proto .gauge) — widget sobre surface blanca. */
  metricCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: spacing.md,
  },
  /** Sección plana con hairline (proto .sec-divided). */
  flatSection: {
    paddingTop: spacing.lg,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  actionsDockDesktop: {
    flexShrink: 0,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: 20,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },

  // ── Mobile (full width bottom sheet / clean design) ──
  sheetBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  sheetCard: {
    width: '100%',
    maxHeight: '92%',
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    zIndex: 1,
  },
  sheetScroll: {
    flexShrink: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
    gap: spacing.lg,
  },
  sheetActionsDock: {
    flexShrink: 0,
    borderTopWidth: 1,
    borderTopColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: 20,
    paddingTop: spacing.md,
    paddingBottom: 20,
    gap: spacing.sm,
  },

  // ── Mobile-specific clean design overrides ──
  heroBandMobile: {
    ...ticketSurfaces.paperSheet,
    padding: spacing.lg,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  heroEyebrowMobile: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  heroNameMobile: {
    fontSize: 17,
    fontWeight: '700',
    color: ticketColors.ink,
    lineHeight: 22,
  },
  heroBalanceMobile: {
    fontSize: 28,
    lineHeight: 34,
    color: ticketColors.ink,
    fontWeight: '900',
    marginTop: 2,
  },
  heroMetaMobile: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
    lineHeight: 16,
  },
  heroStatsMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.sm,
    paddingTop: spacing.xs,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  heroStatsDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  heroStatItem: {
    alignItems: 'flex-start',
  },
  heroStatValue: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  heroStatLabel: {
    fontSize: 10,
    color: ticketColors.ink3,
    marginTop: 1,
  },
  heroStatDivider: {
    width: 1,
    height: 20,
    backgroundColor: ticketColors.ruleSoft,
  },
  statusPillMobile: {
    alignSelf: 'flex-start',
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(34,197,94,0.45)',
    backgroundColor: 'rgba(34,197,94,0.10)',
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  statusPillBlocked: {
    borderColor: ticketColors.bad,
    backgroundColor: ticketColors.warnBg,
  },
  statusPillTextOk: {
    fontSize: 10,
    fontWeight: '800',
    color: ticketColors.ok,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  statusPillTextBlocked: {
    fontSize: 10,
    fontWeight: '800',
    color: ticketColors.bad,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  infoPanelMobile: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    padding: 14,
    gap: 6,
  },
  sectionLabelMobile: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  actionBtnMobile: {
    flexGrow: 1,
    flexBasis: '46%',
    minWidth: 0,
    minHeight: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  actionBtnPrimaryMobile: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  actionBtnDangerMobile: {
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
  },
  actionBtnFull: {
    flexBasis: '100%',
  },
  actionTextMobile: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  actionTextPrimaryMobile: {
    fontSize: 14,
    color: ticketColors.goldInk,
    fontWeight: '800',
  },
  actionTextDangerMobile: {
    fontSize: 14,
    color: ticketColors.bad,
    fontWeight: '700',
  },
});
